import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ImageUpload from '../../components/ImageUpload';
import SanMarSearch from './SanMarSearch';
import DeleteStoreModal from './DeleteStoreModal';
import './StoreEditor.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '/api';

const StoreEditor = () => {
  const { storeId } = useParams();
  const navigate = useNavigate();

  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('products');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [notification, setNotification] = useState(null);

  // Modal states
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Settings form state
  const [settings, setSettings] = useState({
    name: '',
    slug: '',
    password: '',
    deadline: '',
    logo_url: '',
    logo_initials: '',
    contact_email: '',
    description: '',
    pickup_type: 'store',
    completion_email_subject: '',
    completion_email_body: '',
    banner_message: '',
    student_info_type: 'none'
  });

  // Fundraiser state
  const [fundraiserSettings, setFundraiserSettings] = useState({
    enabled: false,
    type: 'percentage',
    percentage: 0,
    flat_amount: 0
  });
  const [fundraiserProducts, setFundraiserProducts] = useState([]);
  const [fundraiserSaving, setFundraiserSaving] = useState(false);

  const showNotification = (message, type) => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  };

  const fetchStore = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/team-stores/admin/${storeId}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setStore(data.store);
        setSettings({
          name: data.store.name || '',
          slug: data.store.slug || '',
          password: data.store.password || '',
          deadline: data.store.deadline || '', // Already in correct format from API (Texas time)
          logo_url: data.store.logo_url || '',
          logo_initials: data.store.logo_initials || '',
          contact_email: data.store.contact_email || '',
          description: data.store.description || '',
          pickup_type: data.store.pickup_type || 'store',
          completion_email_subject: data.store.completion_email_subject || '',
          completion_email_body: data.store.completion_email_body || '',
          banner_message: data.store.banner_message || '',
          student_info_type: data.store.student_info_type || 'none'
        });
        if (data.store.categories?.length > 0 && !selectedCategory) {
          setSelectedCategory(data.store.categories[0]);
        }
      } else {
        showNotification('Error loading store', 'error');
        navigate('/team-store-management');
      }
    } catch (error) {
      console.error('Error fetching store:', error);
      showNotification('Error loading store', 'error');
    } finally {
      setLoading(false);
    }
  }, [storeId, navigate, selectedCategory]);

  useEffect(() => {
    fetchStore();
  }, [fetchStore]);

  // Category CRUD
  const handleSaveCategory = async (categoryData) => {
    try {
      setSaving(true);
      const isEdit = !!editingCategory?.id;
      const url = isEdit
        ? `${API_BASE}/team-stores/admin/${storeId}/categories/${editingCategory.id}`
        : `${API_BASE}/team-stores/admin/${storeId}/categories`;

      const response = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(categoryData)
      });

      if (response.ok) {
        showNotification(`Category ${isEdit ? 'updated' : 'created'} successfully!`, 'success');
        setCategoryModalOpen(false);
        setEditingCategory(null);
        await fetchStore();
      } else {
        const error = await response.json();
        showNotification(error.message || 'Error saving category', 'error');
      }
    } catch (error) {
      showNotification('Error saving category', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Delete this category and all its products?')) return;

    try {
      const response = await fetch(`${API_BASE}/team-stores/admin/${storeId}/categories/${categoryId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        showNotification('Category deleted', 'success');
        if (selectedCategory?.id === categoryId) {
          setSelectedCategory(null);
        }
        await fetchStore();
      } else {
        showNotification('Error deleting category', 'error');
      }
    } catch (error) {
      showNotification('Error deleting category', 'error');
    }
  };

  // Product CRUD
  const handleSaveProduct = async (productData) => {
    try {
      setSaving(true);
      const isEdit = !!editingProduct?.id;
      const url = isEdit
        ? `${API_BASE}/team-stores/admin/${storeId}/products/${editingProduct.id}`
        : `${API_BASE}/team-stores/admin/${storeId}/categories/${selectedCategory.id}/products`;

      const response = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(productData)
      });

      if (response.ok) {
        showNotification(`Product ${isEdit ? 'updated' : 'created'} successfully!`, 'success');
        setProductModalOpen(false);
        setEditingProduct(null);
        await fetchStore();
      } else {
        const error = await response.json();
        showNotification(error.message || 'Error saving product', 'error');
      }
    } catch (error) {
      showNotification('Error saving product', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Delete this product?')) return;

    try {
      const response = await fetch(`${API_BASE}/team-stores/admin/${storeId}/products/${productId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        showNotification('Product deleted', 'success');
        await fetchStore();
      } else {
        showNotification('Error deleting product', 'error');
      }
    } catch (error) {
      showNotification('Error deleting product', 'error');
    }
  };

  // Settings save
  const handleSaveSettings = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const response = await fetch(`${API_BASE}/team-stores/admin/${storeId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: settings.name,
          slug: settings.slug,
          password: settings.password || null,
          deadline: settings.deadline || null,
          logo_url: settings.logo_url || null,
          logo_initials: settings.logo_initials || null,
          contact_email: settings.contact_email || null,
          description: settings.description || null,
          pickup_type: settings.pickup_type || 'store',
          completion_email_subject: settings.completion_email_subject || null,
          completion_email_body: settings.completion_email_body || null,
          banner_message: settings.banner_message || null,
          student_info_type: settings.student_info_type || 'none'
        })
      });

      if (response.ok) {
        showNotification('Settings saved successfully!', 'success');
        await fetchStore();
      } else {
        const error = await response.json();
        showNotification(error.message || 'Error saving settings', 'error');
      }
    } catch (error) {
      showNotification('Error saving settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Fundraiser functions
  const fetchFundraiserData = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/team-stores/admin/${storeId}/fundraiser`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setFundraiserSettings({
          enabled: data.settings.fundraiser_enabled || false,
          type: data.settings.fundraiser_type || 'percentage',
          percentage: parseFloat(data.settings.fundraiser_percentage) || 0,
          flat_amount: parseFloat(data.settings.fundraiser_flat_amount) || 0
        });
        setFundraiserProducts(data.products || []);
      }
    } catch (error) {
      console.error('Error fetching fundraiser data:', error);
    }
  }, [storeId]);

  useEffect(() => {
    if (activeTab === 'fundraiser') {
      fetchFundraiserData();
    }
  }, [activeTab, fetchFundraiserData]);

  const handleSaveFundraiserSettings = async () => {
    try {
      setFundraiserSaving(true);
      const response = await fetch(`${API_BASE}/team-stores/admin/${storeId}/fundraiser`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          fundraiser_enabled: fundraiserSettings.enabled,
          fundraiser_type: fundraiserSettings.type,
          fundraiser_percentage: fundraiserSettings.percentage,
          fundraiser_flat_amount: fundraiserSettings.flat_amount
        })
      });

      if (response.ok) {
        showNotification('Fundraiser settings saved!', 'success');
      } else {
        showNotification('Error saving fundraiser settings', 'error');
      }
    } catch (error) {
      showNotification('Error saving fundraiser settings', 'error');
    } finally {
      setFundraiserSaving(false);
    }
  };

  const handleProductFundraiserToggle = async (productId, excluded) => {
    try {
      const response = await fetch(`${API_BASE}/team-stores/admin/${storeId}/products/${productId}/fundraiser`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ excluded })
      });

      if (response.ok) {
        setFundraiserProducts(prev =>
          prev.map(p => p.id === productId ? { ...p, fundraiser_excluded: excluded } : p)
        );
      } else {
        showNotification('Error updating product', 'error');
      }
    } catch (error) {
      showNotification('Error updating product', 'error');
    }
  };

  const handleProductMarkupOverrideChange = async (productId, { markup_type, markup_value }) => {
    try {
      const response = await fetch(`${API_BASE}/team-stores/admin/${storeId}/products/${productId}/fundraiser`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ markup_type, markup_value })
      });

      if (response.ok) {
        setFundraiserProducts(prev =>
          prev.map(p => p.id === productId
            ? { ...p, fundraiser_markup_type: markup_type, fundraiser_markup_value: markup_value }
            : p
          )
        );
      } else {
        showNotification('Error updating markup override', 'error');
      }
    } catch (error) {
      showNotification('Error updating markup override', 'error');
    }
  };

  const handleBulkFundraiserUpdate = async (action) => {
    try {
      setFundraiserSaving(true);
      const response = await fetch(`${API_BASE}/team-stores/admin/${storeId}/fundraiser/products`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ action })
      });

      if (response.ok) {
        const data = await response.json();
        showNotification(`${data.updated} products updated`, 'success');
        await fetchFundraiserData();
      } else {
        showNotification('Error updating products', 'error');
      }
    } catch (error) {
      showNotification('Error updating products', 'error');
    } finally {
      setFundraiserSaving(false);
    }
  };

  // Store actions
  const handlePublish = async () => {
    try {
      const response = await fetch(`${API_BASE}/team-stores/admin/${storeId}/publish`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        showNotification('Store published!', 'success');
        await fetchStore();
      } else {
        const error = await response.json();
        showNotification(error.message || 'Error publishing', 'error');
      }
    } catch (error) {
      showNotification('Error publishing store', 'error');
    }
  };

  const handleClose = async () => {
    if (!window.confirm('Close this store? Customers will no longer be able to place orders.')) return;
    try {
      const response = await fetch(`${API_BASE}/team-stores/admin/${storeId}/close`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        showNotification('Store closed', 'success');
        await fetchStore();
      } else {
        showNotification('Error closing store', 'error');
      }
    } catch (error) {
      showNotification('Error closing store', 'error');
    }
  };

  const handleReopen = async () => {
    try {
      const response = await fetch(`${API_BASE}/team-stores/admin/${storeId}/reopen`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        showNotification('Store reopened!', 'success');
        await fetchStore();
      } else {
        showNotification('Error reopening store', 'error');
      }
    } catch (error) {
      showNotification('Error reopening store', 'error');
    }
  };

  const handleDeleteStore = async () => {
    try {
      const response = await fetch(`${API_BASE}/team-stores/admin/${storeId}/permanent`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        navigate('/team-store-management');
      } else {
        const error = await response.json();
        showNotification(error.message || 'Error deleting store', 'error');
        setShowDeleteModal(false);
      }
    } catch (error) {
      showNotification('Error deleting store', 'error');
      setShowDeleteModal(false);
    }
  };

  // Reorder categories
  const handleMoveCategory = async (categoryId, direction) => {
    const categories = [...store.categories];
    const index = categories.findIndex(c => c.id === categoryId);
    if ((direction === 'up' && index === 0) || (direction === 'down' && index === categories.length - 1)) {
      return;
    }

    const newIndex = direction === 'up' ? index - 1 : index + 1;
    const [moved] = categories.splice(index, 1);
    categories.splice(newIndex, 0, moved);

    // Build order array with id and sort_order
    const order = categories.map((c, idx) => ({ id: c.id, sort_order: idx }));

    try {
      const response = await fetch(`${API_BASE}/team-stores/admin/${storeId}/categories/reorder`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ order })
      });
      if (!response.ok) {
        throw new Error('Reorder request failed');
      }
      await fetchStore();
    } catch (error) {
      showNotification('Error reordering', 'error');
    }
  };

  // Reorder products
  const handleMoveProduct = async (productId, direction) => {
    const products = [...(selectedCategory?.products || [])];
    const index = products.findIndex(p => p.id === productId);
    if ((direction === 'up' && index === 0) || (direction === 'down' && index === products.length - 1)) {
      return;
    }

    const newIndex = direction === 'up' ? index - 1 : index + 1;
    const [moved] = products.splice(index, 1);
    products.splice(newIndex, 0, moved);

    // Build order array with id and sort_order
    const order = products.map((p, idx) => ({ id: p.id, sort_order: idx }));

    try {
      await fetch(`${API_BASE}/team-stores/admin/${storeId}/categories/${selectedCategory.id}/products/reorder`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ order })
      });
      await fetchStore();
    } catch (error) {
      showNotification('Error reordering', 'error');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    const classes = {
      draft: 'se-badge-draft',
      active: 'se-badge-active',
      closed: 'se-badge-closed'
    };
    return <span className={`se-badge ${classes[status] || ''}`}>{status}</span>;
  };

  if (loading) {
    return (
      <div className="se-container">
        <div className="se-loading">
          <div className="se-spinner"></div>
          <p>Loading store...</p>
        </div>
      </div>
    );
  }

  if (!store) {
    return (
      <div className="se-container">
        <div className="se-error">Store not found</div>
      </div>
    );
  }

  const selectedCategoryProducts = store.categories?.find(c => c.id === selectedCategory?.id)?.products || [];

  return (
    <div className="se-container">
      {notification && (
        <div className={`se-notification se-notification-${notification.type}`}>
          {notification.message}
        </div>
      )}

      {/* Header */}
      <div className="se-header">
        <div className="se-header-left">
          <button className="se-back-btn" onClick={() => navigate('/team-store-management')}>
            ← Back
          </button>
          <div className="se-title">
            <h1>{store.name}</h1>
            {getStatusBadge(store.status)}
          </div>
        </div>
        <div className="se-header-actions">
          {store.status === 'draft' && (
            <button className="se-btn se-btn-success" onClick={handlePublish}>
              Publish Store
            </button>
          )}
          {store.status === 'active' && (
            <>
              <a
                href={`https://fitness1stsports.com/?team_store=${store.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="se-btn se-btn-secondary"
              >
                View Live Store
              </a>
              <button className="se-btn se-btn-warning" onClick={handleClose}>
                Close Store
              </button>
            </>
          )}
          {store.status === 'closed' && (
            <button className="se-btn se-btn-success" onClick={handleReopen}>
              Reopen Store
            </button>
          )}
          <button className="se-btn se-btn-danger" onClick={() => setShowDeleteModal(true)}>
            Delete Store
          </button>
        </div>
      </div>

      {showDeleteModal && (
        <DeleteStoreModal
          store={store}
          onConfirm={handleDeleteStore}
          onCancel={() => setShowDeleteModal(false)}
        />
      )}

      {/* Tabs */}
      <div className="se-tabs">
        <button
          className={`se-tab ${activeTab === 'products' ? 'active' : ''}`}
          onClick={() => setActiveTab('products')}
        >
          Categories & Products
        </button>
        <button
          className={`se-tab ${activeTab === 'fundraiser' ? 'active' : ''}`}
          onClick={() => setActiveTab('fundraiser')}
        >
          Fundraiser
        </button>
        <button
          className={`se-tab ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          Settings
        </button>
      </div>

      {/* Products Tab */}
      {activeTab === 'products' && (
        <div className="se-products-layout">
          {/* Categories Panel */}
          <div className="se-panel se-categories-panel">
            <div className="se-panel-header">
              <h2>Product Categories</h2>
              <button
                className="se-btn se-btn-sm se-btn-primary"
                onClick={() => {
                  setEditingCategory(null);
                  setCategoryModalOpen(true);
                }}
              >
                + Add
              </button>
            </div>
            <div className="se-panel-body">
              {store.categories?.length === 0 ? (
                <div className="se-empty">
                  <p>No categories yet</p>
                  <button
                    className="se-btn se-btn-primary"
                    onClick={() => {
                      setEditingCategory(null);
                      setCategoryModalOpen(true);
                    }}
                  >
                    Add First Category
                  </button>
                </div>
              ) : (
                <ul className="se-category-list">
                  {store.categories.map((category, index) => (
                    <li
                      key={category.id}
                      className={`se-category-item ${selectedCategory?.id === category.id ? 'selected' : ''}`}
                      onClick={() => setSelectedCategory(category)}
                    >
                      <div className="se-category-info">
                        <span className="se-category-name">
                          {category.name}
                          {category.is_package && <span className="se-package-badge">Package</span>}
                        </span>
                        <span className="se-category-count">
                          {category.products?.length || 0} products
                          {category.is_package && category.package_price && ` • $${parseFloat(category.package_price).toFixed(2)}`}
                        </span>
                      </div>
                      <div className="se-category-actions">
                        <button
                          className="se-icon-btn"
                          onClick={(e) => { e.stopPropagation(); handleMoveCategory(category.id, 'up'); }}
                          disabled={index === 0}
                          title="Move Up"
                        >
                          ↑
                        </button>
                        <button
                          className="se-icon-btn"
                          onClick={(e) => { e.stopPropagation(); handleMoveCategory(category.id, 'down'); }}
                          disabled={index === store.categories.length - 1}
                          title="Move Down"
                        >
                          ↓
                        </button>
                        <button
                          className="se-icon-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingCategory(category);
                            setCategoryModalOpen(true);
                          }}
                          title="Edit"
                        >
                          ✏️
                        </button>
                        <button
                          className="se-icon-btn se-icon-btn-danger"
                          onClick={(e) => { e.stopPropagation(); handleDeleteCategory(category.id); }}
                          title="Delete"
                        >
                          🗑️
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Products Panel */}
          <div className="se-panel se-products-panel">
            <div className="se-panel-header">
              <h2>{selectedCategory ? `${selectedCategory.name} Products` : 'Products'}</h2>
              {selectedCategory && (
                <button
                  className="se-btn se-btn-sm se-btn-primary"
                  onClick={() => {
                    setEditingProduct(null);
                    setProductModalOpen(true);
                  }}
                >
                  + Add Product
                </button>
              )}
            </div>
            <div className="se-panel-body">
              {!selectedCategory ? (
                <div className="se-empty">
                  <p>Select a category to view products</p>
                </div>
              ) : selectedCategoryProducts.length === 0 ? (
                <div className="se-empty">
                  <p>No products in this category</p>
                  <button
                    className="se-btn se-btn-primary"
                    onClick={() => {
                      setEditingProduct(null);
                      setProductModalOpen(true);
                    }}
                  >
                    Add First Product
                  </button>
                </div>
              ) : (
                <div className="se-products-grid">
                  {selectedCategoryProducts.map((product, index) => (
                    <div key={product.id} className="se-product-card">
                      {product.image_url && (
                        <div className="se-product-image">
                          <img src={product.image_url} alt={product.name} />
                        </div>
                      )}
                      <div className="se-product-info">
                        <h3>{product.name}</h3>
                        <p className="se-product-price">{formatCurrency(product.price)}</p>
                        <p className="se-product-sizes">Sizes: {product.sizes}</p>
                        {product.colors && (
                          <p className="se-product-colors">Colors: {product.colors}</p>
                        )}
                      </div>
                      <div className="se-product-actions">
                        <button
                          className="se-icon-btn"
                          onClick={() => handleMoveProduct(product.id, 'up')}
                          disabled={index === 0}
                          title="Move Up"
                        >
                          ↑
                        </button>
                        <button
                          className="se-icon-btn"
                          onClick={() => handleMoveProduct(product.id, 'down')}
                          disabled={index === selectedCategoryProducts.length - 1}
                          title="Move Down"
                        >
                          ↓
                        </button>
                        <button
                          className="se-icon-btn"
                          onClick={() => {
                            setEditingProduct(product);
                            setProductModalOpen(true);
                          }}
                          title="Edit"
                        >
                          ✏️
                        </button>
                        <button
                          className="se-icon-btn se-icon-btn-danger"
                          onClick={() => handleDeleteProduct(product.id)}
                          title="Delete"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Fundraiser Tab */}
      {activeTab === 'fundraiser' && (
        <div className="se-fundraiser-panel">
          <div className="se-form-section">
            <h3>Fundraiser Settings</h3>
            <p className="se-hint">Enable fundraiser mode to add a markup to products. The markup goes toward the fundraiser.</p>

            <div className="se-fundraiser-toggle">
              <label className="se-toggle-label">
                <input
                  type="checkbox"
                  checked={fundraiserSettings.enabled}
                  onChange={(e) => setFundraiserSettings(prev => ({ ...prev, enabled: e.target.checked }))}
                />
                <span className="se-toggle-text">Enable Fundraiser Mode</span>
              </label>
            </div>

            {fundraiserSettings.enabled && (
              <>
                <div className="se-form-group se-fundraiser-type">
                  <label>Markup Type</label>
                  <div className="se-radio-group">
                    <label className="se-radio-label">
                      <input
                        type="radio"
                        name="fundraiser_type"
                        checked={fundraiserSettings.type === 'percentage'}
                        onChange={() => setFundraiserSettings(prev => ({ ...prev, type: 'percentage' }))}
                      />
                      <span>Percentage</span>
                    </label>
                    <label className="se-radio-label">
                      <input
                        type="radio"
                        name="fundraiser_type"
                        checked={fundraiserSettings.type === 'flat'}
                        onChange={() => setFundraiserSettings(prev => ({ ...prev, type: 'flat' }))}
                      />
                      <span>Flat Dollar Amount</span>
                    </label>
                  </div>
                </div>

                {fundraiserSettings.type === 'percentage' ? (
                  <div className="se-form-group se-fundraiser-percentage">
                    <label htmlFor="fundraiser_percentage">Fundraiser Percentage</label>
                    <div className="se-percentage-input">
                      <input
                        type="number"
                        id="fundraiser_percentage"
                        value={fundraiserSettings.percentage}
                        onChange={(e) => setFundraiserSettings(prev => ({ ...prev, percentage: parseFloat(e.target.value) || 0 }))}
                        min="0"
                        max="100"
                        step="1"
                      />
                      <span className="se-percentage-symbol">%</span>
                    </div>
                    <span className="se-hint">
                      Example: A $30 item + {fundraiserSettings.percentage}% = ${Math.round(30 * (1 + fundraiserSettings.percentage / 100))} (${Math.round(30 * fundraiserSettings.percentage / 100)} to fundraiser)
                    </span>
                  </div>
                ) : (
                  <div className="se-form-group se-fundraiser-percentage">
                    <label htmlFor="fundraiser_flat_amount">Fundraiser Amount Per Item</label>
                    <div className="se-percentage-input">
                      <span className="se-percentage-symbol">$</span>
                      <input
                        type="number"
                        id="fundraiser_flat_amount"
                        value={fundraiserSettings.flat_amount}
                        onChange={(e) => setFundraiserSettings(prev => ({ ...prev, flat_amount: parseFloat(e.target.value) || 0 }))}
                        min="0"
                        step="0.50"
                      />
                    </div>
                    <span className="se-hint">
                      Example: A $30 item + ${fundraiserSettings.flat_amount.toFixed(2)} flat = ${Math.round(30 + fundraiserSettings.flat_amount)} (${fundraiserSettings.flat_amount.toFixed(2)} to fundraiser)
                    </span>
                  </div>
                )}
              </>
            )}

            <div className="se-form-actions">
              <button
                type="button"
                className="se-btn se-btn-primary"
                onClick={handleSaveFundraiserSettings}
                disabled={fundraiserSaving}
              >
                {fundraiserSaving ? 'Saving...' : 'Save Fundraiser Settings'}
              </button>
            </div>
          </div>

          {fundraiserSettings.enabled && (
            <div className="se-form-section">
              <h3>Product Inclusion</h3>
              <p className="se-hint">Select which products should have the fundraiser markup applied. Excluded products will be sold at their base price. Give a product its own markup to override the store's default rate.</p>

              <div className="se-fundraiser-bulk-actions">
                <button
                  type="button"
                  className="se-btn se-btn-secondary"
                  onClick={() => handleBulkFundraiserUpdate('include_all')}
                  disabled={fundraiserSaving}
                >
                  Include All Products
                </button>
                <button
                  type="button"
                  className="se-btn se-btn-secondary"
                  onClick={() => handleBulkFundraiserUpdate('exclude_all')}
                  disabled={fundraiserSaving}
                >
                  Exclude All Products
                </button>
              </div>

              <div className="se-fundraiser-products">
                {fundraiserProducts.length === 0 ? (
                  <p className="se-empty">No products found. Add products in the Categories & Products tab.</p>
                ) : (
                  <table className="se-fundraiser-table">
                    <thead>
                      <tr>
                        <th>Include</th>
                        <th>Product</th>
                        <th>Category</th>
                        <th>Base Price</th>
                        <th>Markup Override</th>
                        <th>With Fundraiser</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fundraiserProducts.map(product => {
                        const basePrice = parseFloat(product.price) || 0;
                        const effectiveType = product.fundraiser_markup_type || fundraiserSettings.type;
                        const effectiveValue = product.fundraiser_markup_type
                          ? (parseFloat(product.fundraiser_markup_value) || 0)
                          : (fundraiserSettings.type === 'flat' ? fundraiserSettings.flat_amount : fundraiserSettings.percentage);
                        const withFundraiser = effectiveType === 'flat'
                          ? Math.round(basePrice + effectiveValue)
                          : Math.round(basePrice * (1 + effectiveValue / 100));
                        const fundraiserAmount = withFundraiser - basePrice;

                        return (
                          <tr key={product.id} className={product.fundraiser_excluded ? 'se-excluded' : ''}>
                            <td>
                              <input
                                type="checkbox"
                                checked={!product.fundraiser_excluded}
                                onChange={(e) => handleProductFundraiserToggle(product.id, !e.target.checked)}
                              />
                            </td>
                            <td>{product.name}</td>
                            <td>{product.category_name}</td>
                            <td>${basePrice.toFixed(2)}</td>
                            <td>
                              <div className="se-markup-override">
                                <select
                                  value={product.fundraiser_markup_type || ''}
                                  disabled={product.fundraiser_excluded}
                                  onChange={(e) => {
                                    const markupType = e.target.value || null;
                                    const markupValue = markupType ? (product.fundraiser_markup_value || 0) : null;
                                    setFundraiserProducts(prev => prev.map(p => p.id === product.id
                                      ? { ...p, fundraiser_markup_type: markupType, fundraiser_markup_value: markupValue }
                                      : p
                                    ));
                                    handleProductMarkupOverrideChange(product.id, { markup_type: markupType, markup_value: markupValue });
                                  }}
                                >
                                  <option value="">Store Default</option>
                                  <option value="percentage">Percentage</option>
                                  <option value="flat">Flat $</option>
                                </select>
                                {product.fundraiser_markup_type && (
                                  <span className="se-override-value-input">
                                    {product.fundraiser_markup_type === 'flat' && <span>$</span>}
                                    <input
                                      type="number"
                                      min="0"
                                      step={product.fundraiser_markup_type === 'flat' ? '0.50' : '1'}
                                      value={product.fundraiser_markup_value ?? ''}
                                      disabled={product.fundraiser_excluded}
                                      onChange={(e) => {
                                        const val = parseFloat(e.target.value) || 0;
                                        setFundraiserProducts(prev => prev.map(p => p.id === product.id
                                          ? { ...p, fundraiser_markup_value: val }
                                          : p
                                        ));
                                      }}
                                      onBlur={(e) => {
                                        const val = parseFloat(e.target.value) || 0;
                                        handleProductMarkupOverrideChange(product.id, { markup_type: product.fundraiser_markup_type, markup_value: val });
                                      }}
                                    />
                                    {product.fundraiser_markup_type === 'percentage' && <span>%</span>}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td>
                              {product.fundraiser_excluded ? (
                                <span className="se-excluded-price">${basePrice.toFixed(2)}</span>
                              ) : (
                                <span className="se-fundraiser-price">
                                  ${withFundraiser.toFixed(2)}
                                  <small> (+${fundraiserAmount.toFixed(2)})</small>
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="se-settings-panel">
          <form onSubmit={handleSaveSettings}>
            <div className="se-form-section">
              <h3>Basic Information</h3>

              <div className="se-form-group">
                <label htmlFor="name">Store Name</label>
                <input
                  type="text"
                  id="name"
                  value={settings.name}
                  onChange={(e) => setSettings({ ...settings, name: e.target.value })}
                  required
                />
              </div>

              <div className="se-form-group">
                <label htmlFor="slug">URL Slug</label>
                <div className="se-slug-input">
                  <span className="se-slug-prefix">fitness1stsports.com/?team_store=</span>
                  <input
                    type="text"
                    id="slug"
                    value={settings.slug}
                    onChange={(e) => setSettings({ ...settings, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                    required
                  />
                </div>
              </div>

              <div className="se-form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  value={settings.description}
                  onChange={(e) => setSettings({ ...settings, description: e.target.value })}
                  rows={3}
                />
              </div>
            </div>

            <div className="se-form-section">
              <h3>Store Settings</h3>

              <div className="se-form-row">
                <div className="se-form-group">
                  <label htmlFor="password">Password Protection</label>
                  <input
                    type="text"
                    id="password"
                    value={settings.password}
                    onChange={(e) => setSettings({ ...settings, password: e.target.value })}
                    placeholder="Leave blank for no password"
                  />
                </div>

                <div className="se-form-group">
                  <label htmlFor="deadline">Order Deadline</label>
                  <input
                    type="datetime-local"
                    id="deadline"
                    value={settings.deadline}
                    onChange={(e) => setSettings({ ...settings, deadline: e.target.value })}
                  />
                  <span className="se-hint">Central Time (Texas)</span>
                </div>
              </div>

              <div className="se-form-group">
                <label htmlFor="contact_email">Contact Email</label>
                <input
                  type="email"
                  id="contact_email"
                  value={settings.contact_email}
                  onChange={(e) => setSettings({ ...settings, contact_email: e.target.value })}
                  placeholder="team@example.com"
                />
              </div>
            </div>

            <div className="se-form-section">
              <h3>Branding</h3>

              <div className="se-form-group">
                <ImageUpload
                  label="Store Logo"
                  value={settings.logo_url}
                  onChange={(url) => setSettings({ ...settings, logo_url: url })}
                  placeholder="Click or drag logo image here"
                />
              </div>

              <div className="se-form-group">
                <label htmlFor="logo_initials">Logo Initials (Fallback)</label>
                <input
                  type="text"
                  id="logo_initials"
                  value={settings.logo_initials}
                  onChange={(e) => setSettings({ ...settings, logo_initials: e.target.value.toUpperCase().substring(0, 4) })}
                  maxLength={4}
                  placeholder="KT"
                />
                <span className="se-hint">Displayed if no logo image is uploaded</span>
              </div>

              {settings.logo_initials && !settings.logo_url && (
                <div className="se-logo-preview">
                  <span>Preview:</span>
                  <div className="se-initials-preview">{settings.logo_initials}</div>
                </div>
              )}
            </div>

            <div className="se-form-section">
              <h3>Store Banner</h3>
              <p className="se-section-desc">This message appears at the top of your online store.</p>

              <div className="se-form-group">
                <label htmlFor="banner_message">
                  Banner Message <span className="se-optional">(optional)</span>
                </label>
                <textarea
                  id="banner_message"
                  value={settings.banner_message}
                  onChange={(e) => setSettings({ ...settings, banner_message: e.target.value })}
                  rows={4}
                  placeholder={settings.pickup_type === 'store'
                    ? `During checkout, please select "In Store Pickup". After the store closes on {deadline_date}, orders will begin processing and you'll receive an email when your order is ready.\n\nThanks,\n-Fitness 1st Sports Team`
                    : `After the store closes on {deadline_date}, orders will be processed and delivered to your coach/team contact for distribution.\n\nThanks,\n-Fitness 1st Sports Team`}
                />
                <span className="se-hint">
                  Placeholders: {'{store_name}'}, {'{deadline}'}, {'{deadline_date}'}, {'{deadline_time}'}
                </span>
              </div>

              {settings.banner_message && (
                <div className="se-banner-preview">
                  <h4>Banner Preview</h4>
                  <div className="se-banner-preview-box">
                    {settings.banner_message
                      .replace(/\{store_name\}/gi, settings.name || 'Store Name')
                      .replace(/\{deadline\}/gi, settings.deadline ? new Date(settings.deadline).toLocaleString('en-US', { month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '[No deadline set]')
                      .replace(/\{deadline_date\}/gi, settings.deadline ? new Date(settings.deadline).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '[No deadline set]')
                      .replace(/\{deadline_time\}/gi, settings.deadline ? new Date(settings.deadline).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '[No time set]')
                    }
                  </div>
                </div>
              )}
            </div>

            <div className="se-form-section">
              <h3>Student Info</h3>
              <p className="se-section-desc">
                Default for whether products in this store collect a student name and grade at
                checkout (e.g. for classroom distribution). Individual products can still override
                this in their own settings.
              </p>

              <div className="se-form-group">
                <div className="se-radio-group">
                  <label className="se-radio-option">
                    <input
                      type="radio"
                      name="student_info_type"
                      value="none"
                      checked={settings.student_info_type === 'none'}
                      onChange={(e) => setSettings({ ...settings, student_info_type: e.target.value })}
                    />
                    <span className="se-radio-label">
                      <strong>None</strong>
                      <small>Don't collect student name/grade by default</small>
                    </span>
                  </label>
                  <label className="se-radio-option">
                    <input
                      type="radio"
                      name="student_info_type"
                      value="required"
                      checked={settings.student_info_type === 'required'}
                      onChange={(e) => setSettings({ ...settings, student_info_type: e.target.value })}
                    />
                    <span className="se-radio-label">
                      <strong>Required</strong>
                      <small>Customers must enter a student name/grade at checkout</small>
                    </span>
                  </label>
                  <label className="se-radio-option">
                    <input
                      type="radio"
                      name="student_info_type"
                      value="optional"
                      checked={settings.student_info_type === 'optional'}
                      onChange={(e) => setSettings({ ...settings, student_info_type: e.target.value })}
                    />
                    <span className="se-radio-label">
                      <strong>Optional</strong>
                      <small>Customers may enter a student name/grade at checkout</small>
                    </span>
                  </label>
                </div>
              </div>
            </div>

            <div className="se-form-section">
              <h3>Order Completion</h3>
              <p className="se-section-desc">Configure how customers are notified when their orders are ready.</p>

              <div className="se-form-group">
                <label>Pickup Type</label>
                <div className="se-radio-group">
                  <label className="se-radio-option">
                    <input
                      type="radio"
                      name="pickup_type"
                      value="store"
                      checked={settings.pickup_type === 'store'}
                      onChange={(e) => setSettings({ ...settings, pickup_type: e.target.value })}
                    />
                    <span className="se-radio-label">
                      <strong>Store Pickup</strong>
                      <small>Customer picks up at Fitness First Sports</small>
                    </span>
                  </label>
                  <label className="se-radio-option">
                    <input
                      type="radio"
                      name="pickup_type"
                      value="coach"
                      checked={settings.pickup_type === 'coach'}
                      onChange={(e) => setSettings({ ...settings, pickup_type: e.target.value })}
                    />
                    <span className="se-radio-label">
                      <strong>Coach/Parent Pickup</strong>
                      <small>Coach or team parent distributes to customers</small>
                    </span>
                  </label>
                </div>
              </div>

              <div className="se-form-group">
                <label htmlFor="completion_email_subject">
                  Custom Email Subject <span className="se-optional">(optional)</span>
                </label>
                <input
                  type="text"
                  id="completion_email_subject"
                  value={settings.completion_email_subject}
                  onChange={(e) => setSettings({ ...settings, completion_email_subject: e.target.value })}
                  placeholder={settings.pickup_type === 'store'
                    ? 'Your order is ready for pickup!'
                    : 'Your order has been delivered to your coach!'}
                />
                <span className="se-hint">Leave blank to use default based on pickup type</span>
              </div>

              <div className="se-form-group">
                <label htmlFor="completion_email_body">
                  Custom Email Message <span className="se-optional">(optional)</span>
                </label>
                <textarea
                  id="completion_email_body"
                  value={settings.completion_email_body}
                  onChange={(e) => setSettings({ ...settings, completion_email_body: e.target.value })}
                  rows={6}
                  placeholder={settings.pickup_type === 'store'
                    ? `Your order is ready for pickup at Fitness First Sports!\n\nStore Hours:\nMon-Fri: 10am-6pm\nSat: 10am-4pm\n\n141 W Water St #A100\nKerrville, TX 78028`
                    : `Your order has been delivered to your coach/team parent for distribution.\n\nPlease coordinate with your team contact to pick up your items.`}
                />
                <span className="se-hint">
                  This message will be included in the order completion email. Leave blank to use default.
                </span>
              </div>

              <div className="se-email-preview">
                <h4>Email Preview</h4>
                <div className="se-email-preview-box">
                  <div className="se-email-subject">
                    <strong>Subject:</strong> {settings.completion_email_subject || (settings.pickup_type === 'store'
                      ? 'Your order is ready for pickup!'
                      : 'Your order has been delivered to your coach!')}
                  </div>
                  <div className="se-email-body">
                    {settings.completion_email_body || (settings.pickup_type === 'store'
                      ? `Your order is ready for pickup at Fitness First Sports!\n\nStore Hours:\nMon-Fri: 10am-6pm\nSat: 10am-4pm\n\n141 W Water St #A100\nKerrville, TX 78028`
                      : `Your order has been delivered to your coach/team parent for distribution.\n\nPlease coordinate with your team contact to pick up your items.`)}
                  </div>
                </div>
              </div>
            </div>

            <div className="se-form-actions">
              <button type="submit" className="se-btn se-btn-primary" disabled={saving}>
                {saving ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Category Modal */}
      {categoryModalOpen && (
        <CategoryModal
          category={editingCategory}
          onSave={handleSaveCategory}
          onClose={() => {
            setCategoryModalOpen(false);
            setEditingCategory(null);
          }}
          saving={saving}
        />
      )}

      {/* Product Modal */}
      {productModalOpen && selectedCategory && (
        <ProductModal
          product={editingProduct}
          isPackage={selectedCategory.is_package}
          storeStudentInfoType={store?.student_info_type || 'none'}
          onSave={handleSaveProduct}
          onClose={() => {
            setProductModalOpen(false);
            setEditingProduct(null);
          }}
          saving={saving}
        />
      )}
    </div>
  );
};

// Category Modal Component
const CategoryModal = ({ category, onSave, onClose, saving }) => {
  const [formData, setFormData] = useState({
    name: category?.name || '',
    description: category?.description || '',
    is_package: category?.is_package || false,
    package_price: category?.package_price || '',
    package_min_qty: category?.package_min_qty || '',
    package_max_qty: category?.package_max_qty || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...formData,
      package_price: formData.is_package && formData.package_price ? parseFloat(formData.package_price) : null,
      package_min_qty: formData.is_package && formData.package_min_qty ? parseInt(formData.package_min_qty) : null,
      package_max_qty: formData.is_package && formData.package_max_qty ? parseInt(formData.package_max_qty) : null
    });
  };

  return (
    <div className="se-modal-overlay" onClick={onClose}>
      <div className="se-modal" onClick={(e) => e.stopPropagation()}>
        <div className="se-modal-header">
          <h2>{category ? 'Edit Category' : 'Add Category'}</h2>
          <button className="se-modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit} className="se-modal-body">
          <div className="se-form-group">
            <label htmlFor="cat-name">Category Name</label>
            <input
              type="text"
              id="cat-name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              autoFocus
            />
          </div>
          <div className="se-form-group">
            <label htmlFor="cat-desc">Description (optional)</label>
            <textarea
              id="cat-desc"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={2}
            />
          </div>

          <div className="se-form-divider">
            <span>Package Options</span>
          </div>

          <div className="se-form-group se-checkbox-group">
            <label className="se-checkbox-label">
              <input
                type="checkbox"
                checked={formData.is_package}
                onChange={(e) => setFormData({ ...formData, is_package: e.target.checked })}
              />
              <span className="se-checkbox-text">This is a Package</span>
            </label>
            <span className="se-field-hint">
              Packages have a fixed total price. Customers select items from the package but pay one price.
            </span>
          </div>

          {formData.is_package && (
            <>
              <div className="se-form-group">
                <label htmlFor="package-price">Package Price ($)</label>
                <input
                  type="number"
                  id="package-price"
                  step="0.01"
                  min="0"
                  value={formData.package_price}
                  onChange={(e) => setFormData({ ...formData, package_price: e.target.value })}
                  placeholder="99.99"
                />
              </div>
              <div className="se-form-row">
                <div className="se-form-group">
                  <label htmlFor="package-min-qty">Minimum Quantity</label>
                  <input
                    type="number"
                    id="package-min-qty"
                    min="1"
                    value={formData.package_min_qty}
                    onChange={(e) => setFormData({ ...formData, package_min_qty: e.target.value })}
                    placeholder="No minimum"
                  />
                  <span className="se-field-hint">Minimum items customer must select</span>
                </div>
                <div className="se-form-group">
                  <label htmlFor="package-max-qty">Maximum Quantity</label>
                  <input
                    type="number"
                    id="package-max-qty"
                    min="1"
                    value={formData.package_max_qty}
                    onChange={(e) => setFormData({ ...formData, package_max_qty: e.target.value })}
                    placeholder="No maximum"
                  />
                  <span className="se-field-hint">Maximum items customer can select</span>
                </div>
              </div>
            </>
          )}

          <div className="se-modal-footer">
            <button type="button" className="se-btn se-btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="se-btn se-btn-primary" disabled={saving}>
              {saving ? 'Saving...' : (category ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Helper to parse sizes string and size_pricing object into array
const parseSizesWithPricing = (sizesStr, sizePricing = {}) => {
  if (!sizesStr) return [];
  return sizesStr.split(',').map(s => s.trim()).filter(s => s.length > 0).map(size => ({
    size,
    upcharge: parseFloat(sizePricing[size]) || 0
  }));
};

// Common size presets
const SIZE_PRESETS = {
  'Youth': ['YXS', 'YS', 'YM', 'YL', 'YXL'],
  'Adult': ['S', 'M', 'L', 'XL', '2XL', '3XL'],
  'Adult Extended': ['S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL'],
  'Youth + Adult': ['YS', 'YM', 'YL', 'YXL', 'S', 'M', 'L', 'XL', '2XL']
};

// Sortable Size Row Component
const SortableSizeRow = ({ id, entry, index, onUpdateUpcharge, onRemove }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className={`se-size-row ${isDragging ? 'dragging' : ''}`}>
      <span className="se-size-drag-handle" {...attributes} {...listeners}>
        ⋮⋮
      </span>
      <span className="se-size-name">{entry.size}</span>
      <div className="se-size-upcharge">
        <span className="se-upcharge-prefix">+$</span>
        <input
          type="number"
          value={entry.upcharge || ''}
          onChange={(e) => onUpdateUpcharge(index, e.target.value)}
          placeholder="0.00"
          step="0.01"
          min="0"
        />
      </div>
      <button
        type="button"
        className="se-size-remove"
        onClick={() => onRemove(index)}
        title="Remove size"
      >
        ×
      </button>
    </div>
  );
};

// Product Modal Component
const ProductModal = ({ product, isPackage, storeStudentInfoType, onSave, onClose, saving }) => {
  // Search mode: 'sanmar' or 'manual' (only for new products)
  const [searchMode, setSearchMode] = useState(!product ? 'sanmar' : 'manual');

  const [formData, setFormData] = useState({
    name: product?.name || '',
    price: product?.price || '',
    colors: product?.colors || '',
    image_url: product?.image_url || '',
    color_images: product?.color_images || {},
    designs: product?.designs || '',
    design_images: product?.design_images || {},
    design_selectable: product?.design_selectable !== undefined ? product.design_selectable : true,
    size_selectable: product?.size_selectable !== undefined ? product.size_selectable : true,
    color_selectable: product?.color_selectable !== undefined ? product.color_selectable : true,
    max_qty: product?.max_qty || '',
    sanmar_style: product?.sanmar_style || '',
    sanmar_data: product?.sanmar_data || null,
    // Personalization: 'none' | 'required' | 'optional'
    // Backward compat: if old flags exist, treat as 'required'
    personalization_type: product?.custom_fields?.personalization_type ||
      ((product?.custom_fields?.needs_jersey_number || product?.custom_fields?.needs_last_name) ? 'required' : 'none'),
    personalization_price: product?.custom_fields?.personalization_price || 10,
    include_number: product?.custom_fields?.include_number !== undefined ? product.custom_fields.include_number : true,
    // Student info: 'inherit' (use store default) | 'none' | 'required' | 'optional'
    student_info_type: product?.custom_fields?.student_info_type ?? 'inherit'
  });

  // Sizes with upcharges as array of {size, upcharge}
  const [sizeEntries, setSizeEntries] = useState(() =>
    parseSizesWithPricing(product?.sizes, product?.size_pricing)
  );
  const [newSize, setNewSize] = useState('');

  const addSize = (sizeName) => {
    const trimmed = sizeName.trim();
    if (trimmed && !sizeEntries.some(e => e.size.toLowerCase() === trimmed.toLowerCase())) {
      setSizeEntries([...sizeEntries, { size: trimmed, upcharge: 0 }]);
    }
    setNewSize('');
  };

  const removeSize = (index) => {
    setSizeEntries(sizeEntries.filter((_, i) => i !== index));
  };

  const updateUpcharge = (index, value) => {
    const updated = [...sizeEntries];
    updated[index].upcharge = parseFloat(value) || 0;
    setSizeEntries(updated);
  };

  const addPreset = (presetName) => {
    const presetSizes = SIZE_PRESETS[presetName];
    const existingSizes = new Set(sizeEntries.map(e => e.size.toLowerCase()));
    const newEntries = presetSizes
      .filter(s => !existingSizes.has(s.toLowerCase()))
      .map(s => ({ size: s, upcharge: 0 }));
    setSizeEntries([...sizeEntries, ...newEntries]);
  };

  // Drag and drop sensors for size reordering
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleSizeDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setSizeEntries((items) => {
        const oldIndex = items.findIndex((item) => item.size === active.id);
        const newIndex = items.findIndex((item) => item.size === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  // Parse colors into an array for color-specific image uploads
  const colorList = formData.colors
    ? formData.colors.split(',').map(c => c.trim()).filter(c => c.length > 0)
    : [];

  // Parse designs into an array for design-specific image uploads
  const designList = formData.designs
    ? formData.designs.split(',').map(d => d.trim()).filter(d => d.length > 0)
    : [];

  const handleColorImageChange = (color, url) => {
    setFormData({
      ...formData,
      color_images: {
        ...formData.color_images,
        [color]: url
      }
    });
  };

  const handleDesignImageChange = (design, url) => {
    setFormData({
      ...formData,
      design_images: {
        ...formData.design_images,
        [design]: url
      }
    });
  };

  const handleSanMarSelect = (sanmarProduct) => {
    // Populate form with SanMar data
    setFormData({
      ...formData,
      name: sanmarProduct.name || '',
      price: sanmarProduct.price || '',
      colors: sanmarProduct.colors || '',
      image_url: sanmarProduct.image_url || '',
      color_images: sanmarProduct.color_images || {},
      sanmar_style: sanmarProduct.sanmar_style || '',
      sanmar_data: sanmarProduct.sanmar_data || null
    });
    // Parse SanMar sizes into size entries (all with 0 upcharge)
    if (sanmarProduct.sizes) {
      const sizes = sanmarProduct.sizes.split(',').map(s => s.trim()).filter(s => s);
      setSizeEntries(sizes.map(size => ({ size, upcharge: 0 })));
    }
    // Switch to manual mode to allow editing
    setSearchMode('manual');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Validate at least one size
    if (sizeEntries.length === 0) {
      alert('Please add at least one size');
      return;
    }
    // Convert sizeEntries to sizes string and size_pricing object
    const sizes = sizeEntries.map(e => e.size).join(', ');
    const size_pricing = {};
    sizeEntries.forEach(entry => {
      if (entry.upcharge > 0) {
        size_pricing[entry.size] = entry.upcharge;
      }
    });

    // Build custom_fields with personalization settings
    const custom_fields = {
      ...(product?.custom_fields || {}),
      personalization_type: formData.personalization_type,
      personalization_price: formData.personalization_type === 'optional' ? formData.personalization_price : null,
      include_number: formData.include_number,
      // Keep backward compat flags for existing integrations
      needs_jersey_number: formData.personalization_type !== 'none' && formData.include_number,
      needs_last_name: formData.personalization_type !== 'none',
      student_info_type: formData.student_info_type
    };

    onSave({
      ...formData,
      sizes,
      size_pricing,
      price: parseFloat(formData.price),
      color_images: formData.color_images,
      design_images: formData.design_images,
      max_qty: isPackage && formData.max_qty ? parseInt(formData.max_qty) : null,
      sanmar_style: formData.sanmar_style || null,
      sanmar_data: formData.sanmar_data || null,
      custom_fields
    });
  };

  const isNewProduct = !product;

  return (
    <div className="se-modal-overlay" onClick={onClose}>
      <div className="se-modal se-modal-lg" onClick={(e) => e.stopPropagation()}>
        <div className="se-modal-header">
          <h2>{product ? 'Edit Product' : 'Add Product'}</h2>
          <button className="se-modal-close" onClick={onClose}>×</button>
        </div>

        {/* Mode Toggle for new products */}
        {isNewProduct && (
          <div className="se-mode-toggle">
            <button
              type="button"
              className={`se-mode-btn ${searchMode === 'sanmar' ? 'active' : ''}`}
              onClick={() => setSearchMode('sanmar')}
            >
              Search SanMar
            </button>
            <button
              type="button"
              className={`se-mode-btn ${searchMode === 'manual' ? 'active' : ''}`}
              onClick={() => setSearchMode('manual')}
            >
              Manual Entry
            </button>
          </div>
        )}

        {/* SanMar Search Mode */}
        {isNewProduct && searchMode === 'sanmar' && (
          <div className="se-modal-body">
            <SanMarSearch
              onSelectProduct={handleSanMarSelect}
              onCancel={() => setSearchMode('manual')}
            />
          </div>
        )}

        {/* Manual Entry Mode (or editing existing product) */}
        {(searchMode === 'manual' || product) && (
          <form onSubmit={handleSubmit} className="se-modal-body">
            {/* SanMar reference badge */}
            {formData.sanmar_style && (
              <div className="se-sanmar-badge">
                Imported from SanMar: <strong>{formData.sanmar_style}</strong>
              </div>
            )}

            <div className="se-form-group">
              <label htmlFor="prod-name">Product Name</label>
              <input
                type="text"
                id="prod-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Performance T-Shirt"
                required
                autoFocus={searchMode === 'manual'}
              />
            </div>

            <div className="se-form-row">
              <div className="se-form-group">
                <label htmlFor="prod-price">Price {isPackage && <span className="se-hint-inline">(for reference only)</span>}</label>
                <input
                  type="number"
                  id="prod-price"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  step="0.01"
                  min="0"
                  placeholder="29.99"
                  required
                />
              </div>

            </div>

            {/* Sizes with optional upcharges */}
            <div className="se-form-group">
              <label>Sizes</label>
              <div className="se-size-presets">
                {Object.keys(SIZE_PRESETS).map(preset => (
                  <button
                    key={preset}
                    type="button"
                    className="se-preset-btn"
                    onClick={() => addPreset(preset)}
                  >
                    + {preset}
                  </button>
                ))}
              </div>

              {sizeEntries.length > 0 && (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleSizeDragEnd}
                >
                  <div className="se-size-list">
                    <div className="se-size-header">
                      <span></span>
                      <span>Size</span>
                      <span>Upcharge</span>
                      <span></span>
                    </div>
                    <SortableContext
                      items={sizeEntries.map(e => e.size)}
                      strategy={verticalListSortingStrategy}
                    >
                      {sizeEntries.map((entry, index) => (
                        <SortableSizeRow
                          key={entry.size}
                          id={entry.size}
                          entry={entry}
                          index={index}
                          onUpdateUpcharge={updateUpcharge}
                          onRemove={removeSize}
                        />
                      ))}
                    </SortableContext>
                  </div>
                </DndContext>
              )}

              <div className="se-size-add">
                <input
                  type="text"
                  value={newSize}
                  onChange={(e) => setNewSize(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addSize(newSize);
                    }
                  }}
                  placeholder="Add custom size..."
                />
                <button
                  type="button"
                  className="se-size-add-btn"
                  onClick={() => addSize(newSize)}
                  disabled={!newSize.trim()}
                >
                  Add
                </button>
              </div>
              <span className="se-hint">Drag sizes to reorder. Set optional upcharges for larger sizes (e.g., 2XL +$2.00)</span>
            </div>

            <div className="se-form-group">
              <label htmlFor="prod-colors">Colors (optional)</label>
              <input
                type="text"
                id="prod-colors"
                value={formData.colors}
                onChange={(e) => setFormData({ ...formData, colors: e.target.value })}
                placeholder="Black, Navy, Royal"
              />
              <span className="se-hint">Comma-separated. Leave blank if not applicable.</span>
            </div>

            <div className="se-form-group">
              <ImageUpload
                label="Default Product Image"
                value={formData.image_url}
                onChange={(url) => setFormData({ ...formData, image_url: url })}
                placeholder="Click or drag product image here"
              />
            </div>

            {colorList.length > 0 && (
              <>
                <div className="se-form-divider">
                  <span>Color-Specific Images</span>
                </div>
                <span className="se-field-hint" style={{ marginBottom: '12px', display: 'block' }}>
                  Upload different images for each color variation. Leave blank to use the default image.
                </span>
                <div className="se-color-images-grid">
                  {colorList.map((color) => (
                    <div key={color} className="se-color-image-item">
                      <ImageUpload
                        label={color}
                        value={formData.color_images[color] || ''}
                        onChange={(url) => handleColorImageChange(color, url)}
                        placeholder={`Image for ${color}`}
                        compact
                      />
                    </div>
                  ))}
                </div>
              </>
            )}

            <div className="se-form-divider">
              <span>Design Variations</span>
            </div>

            <div className="se-form-group">
              <label htmlFor="prod-designs">Designs (optional)</label>
              <input
                type="text"
                id="prod-designs"
                value={formData.designs}
                onChange={(e) => setFormData({ ...formData, designs: e.target.value })}
                placeholder="Home Jersey, Away Jersey, Practice"
              />
              <span className="se-hint">Comma-separated design names. Each design can have its own image.</span>
            </div>

            {designList.length > 0 && (
              <>
                <span className="se-field-hint" style={{ marginBottom: '12px', display: 'block' }}>
                  Upload an image for each design variation. These images will be shown to customers.
                </span>
                <div className="se-color-images-grid">
                  {designList.map((design) => (
                    <div key={design} className="se-color-image-item">
                      <ImageUpload
                        label={design}
                        value={formData.design_images[design] || ''}
                        onChange={(url) => handleDesignImageChange(design, url)}
                        placeholder={`Image for ${design}`}
                        compact
                      />
                    </div>
                  ))}
                </div>
              </>
            )}

            <div className="se-form-divider">
              <span>Name & Number Personalization</span>
            </div>
            <span className="se-field-hint" style={{ marginBottom: '12px', display: 'block' }}>
              Allow customers to add a jersey number and last name to this product.
            </span>
            <div className="se-personalization-options">
              <label className="se-radio-label">
                <input
                  type="radio"
                  name="personalization_type"
                  value="none"
                  checked={formData.personalization_type === 'none'}
                  onChange={() => setFormData({ ...formData, personalization_type: 'none' })}
                />
                <span className="se-radio-text">None</span>
              </label>
              <label className="se-radio-label">
                <input
                  type="radio"
                  name="personalization_type"
                  value="required"
                  checked={formData.personalization_type === 'required'}
                  onChange={() => setFormData({ ...formData, personalization_type: 'required' })}
                />
                <span className="se-radio-text">Required (included in price)</span>
              </label>
              <label className="se-radio-label">
                <input
                  type="radio"
                  name="personalization_type"
                  value="optional"
                  checked={formData.personalization_type === 'optional'}
                  onChange={() => setFormData({ ...formData, personalization_type: 'optional' })}
                />
                <span className="se-radio-text">Optional (customer pays extra)</span>
              </label>
            </div>
            {formData.personalization_type !== 'none' && (
              <div className="se-form-row" style={{ marginTop: '12px' }}>
                <div className="se-form-group se-checkbox-group">
                  <label className="se-checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.include_number}
                      onChange={(e) => setFormData({ ...formData, include_number: e.target.checked })}
                    />
                    <span className="se-checkbox-text">Include jersey number field (uncheck for name-only products)</span>
                  </label>
                </div>
              </div>
            )}
            {formData.personalization_type === 'optional' && (
              <div className="se-form-row" style={{ marginTop: '12px' }}>
                <div className="se-form-group" style={{ maxWidth: '200px' }}>
                  <label className="se-form-label">Personalization Price</label>
                  <div className="se-price-input">
                    <span className="se-price-prefix">$</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.personalization_price}
                      onChange={(e) => setFormData({ ...formData, personalization_price: parseFloat(e.target.value) || 0 })}
                      placeholder="10.00"
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="se-form-divider">
              <span>Student Info</span>
            </div>
            <span className="se-field-hint" style={{ marginBottom: '12px', display: 'block' }}>
              Allow customers to add a student name and grade to this product (e.g. for classroom distribution).
              Defaults to the store's setting (Store Settings tab) unless overridden here.
            </span>
            <div className="se-personalization-options">
              <label className="se-radio-label">
                <input
                  type="radio"
                  name="student_info_type"
                  value="inherit"
                  checked={formData.student_info_type === 'inherit'}
                  onChange={() => setFormData({ ...formData, student_info_type: 'inherit' })}
                />
                <span className="se-radio-text">Use store setting ({storeStudentInfoType || 'none'})</span>
              </label>
              <label className="se-radio-label">
                <input
                  type="radio"
                  name="student_info_type"
                  value="none"
                  checked={formData.student_info_type === 'none'}
                  onChange={() => setFormData({ ...formData, student_info_type: 'none' })}
                />
                <span className="se-radio-text">None</span>
              </label>
              <label className="se-radio-label">
                <input
                  type="radio"
                  name="student_info_type"
                  value="required"
                  checked={formData.student_info_type === 'required'}
                  onChange={() => setFormData({ ...formData, student_info_type: 'required' })}
                />
                <span className="se-radio-text">Required</span>
              </label>
              <label className="se-radio-label">
                <input
                  type="radio"
                  name="student_info_type"
                  value="optional"
                  checked={formData.student_info_type === 'optional'}
                  onChange={() => setFormData({ ...formData, student_info_type: 'optional' })}
                />
                <span className="se-radio-text">Optional</span>
              </label>
            </div>

            {isPackage && (
              <>
                <div className="se-form-divider">
                  <span>Package Item Options</span>
                </div>
                <div className="se-form-row">
                  <div className="se-form-group se-checkbox-group">
                    <label className="se-checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.size_selectable}
                        onChange={(e) => setFormData({ ...formData, size_selectable: e.target.checked })}
                      />
                      <span className="se-checkbox-text">Customer can select size</span>
                    </label>
                  </div>
                  <div className="se-form-group se-checkbox-group">
                    <label className="se-checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.color_selectable}
                        onChange={(e) => setFormData({ ...formData, color_selectable: e.target.checked })}
                      />
                      <span className="se-checkbox-text">Customer can select color</span>
                    </label>
                  </div>
                  {designList.length > 0 && (
                    <div className="se-form-group se-checkbox-group">
                      <label className="se-checkbox-label">
                        <input
                          type="checkbox"
                          checked={formData.design_selectable}
                          onChange={(e) => setFormData({ ...formData, design_selectable: e.target.checked })}
                        />
                        <span className="se-checkbox-text">Customer can select design</span>
                      </label>
                    </div>
                  )}
                </div>
                <span className="se-field-hint">
                  Uncheck options to lock the selection for this item in the package.
                </span>
                <div className="se-form-group" style={{ marginTop: '16px' }}>
                  <label htmlFor="prod-max-qty">Max Quantity Per Customer</label>
                  <input
                    type="number"
                    id="prod-max-qty"
                    min="1"
                    value={formData.max_qty}
                    onChange={(e) => setFormData({ ...formData, max_qty: e.target.value })}
                    placeholder="Leave blank for no limit"
                  />
                  <span className="se-hint">Maximum quantity of this item a customer can select in the package</span>
                </div>
              </>
            )}

            <div className="se-modal-footer">
              <button type="button" className="se-btn se-btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="se-btn se-btn-primary" disabled={saving}>
                {saving ? 'Saving...' : (product ? 'Update Product' : 'Add Product')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default StoreEditor;
